import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { formatISO } from 'date-fns';
import { PermissionKey, PermissionModel, SavePermissionRequest } from 'hx-services';
import { PermissionService } from '@adm-web-app/services/permission.service';
import { NotificationService } from '@adm-web-app/services/notification.service';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NgFor, NgIf } from '@angular/common';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzDatePickerComponent, NzRangePickerComponent } from 'ng-zorro-antd/date-picker';
import { HxGroupSelectComponent, HxOrgSelectComponent, HxStoreSelectComponent, HxUserSelectComponent } from 'hx-components';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';

@Component({
    selector: 'hc-permission-edit',
    templateUrl: './permission-edit.component.html',
    styleUrls: ['./permission-edit.component.less'],
  imports: [TranslocoDirective, NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, NzSelectComponent, NgFor, NzOptionComponent, NzRadioGroupComponent, NzRadioComponent, NzDatePickerComponent, NzRangePickerComponent, HxGroupSelectComponent, NgIf, HxStoreSelectComponent, HxUserSelectComponent, HxOrgSelectComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, TranslocoPipe]
})
export class PermissionEditComponent implements OnInit, OnDestroy {

  permission?: PermissionModel;
  isEdit = false;
  isCreate = false;
  form = new FormGroup({
    key: new FormControl<PermissionKey | null>(null, [Validators.required]),
    containerClass: new FormControl<string | null>(null, [Validators.required]),
    containerId: new FormControl<number | null>(null, Validators.required),
    active: new FormControl<boolean | null>(true, [Validators.required]),
    dateRange: new FormControl<Date[] | null>(null),
    excludeUserIds: new FormControl<number[] | null>(null),
    resourceClass: new FormControl<string | null>(null),
    resourceIds: new FormControl<number[] | null>(null),
    groupId: new FormControl<number | null>(null, Validators.required),
  });
  dates?: Date[];

  containerClassOptions = ['User', 'Store', 'Organization'];
  resourceClassOptions = ['Store', 'Organization'];
  keyOptions: PermissionKey[] = [PermissionKey.price_upsert_permission, PermissionKey.read_money_flow_report];
  permissionOptions = Object.keys(PermissionKey).map(key => ({key: `enum.PermissionKey.${key}`, value: key}));
  private $destroyed = new Subject<void>();

  constructor(
    private aRoute: ActivatedRoute,
    private modal: NzModalService,
    private permissionService: PermissionService,
    private tr: TranslocoService,
    private nt: NotificationService,
    private router: Router,
  ) {
  }

  ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.permission = routeData['permission'] as PermissionModel;
    if (this.permission) {
      this.isEdit = true;
    } else {
      this.isCreate = true;
    }

    this.form.patchValue({
      key: this.permission.key ?? null,
      containerClass: this.permission.containerClass,
      containerId: this.permission.containerId,
      active: this.permission.active,
      excludeUserIds: this.permission.excludeUserIds,
      resourceClass: this.permission.resourceClass,
      resourceIds: this.permission.resourceIds,
      groupId: this.permission.groupId,
    });

    if (this.permission.startDate && this.permission.endDate) {
      this.form.patchValue({
        dateRange: [new Date(this.permission.startDate), new Date(this.permission.endDate)]
      });
    }
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      try {
        const formModel = this.form.value;

        console.log('formModel', formModel);

        if (!formModel.key || !formModel.containerClass || !formModel.containerId || !formModel.groupId) {
          return;
        }
        if (formModel.containerClass === 'User') {
          formModel.excludeUserIds = [];
        }
        const model: SavePermissionRequest = {
          id: this.permission?.id,
          active: formModel.active ?? false,
          containerClass: formModel.containerClass,
          containerId: formModel.containerId,
          excludeUserIds: formModel.excludeUserIds || [],
          resourceClass: formModel.resourceClass ?? undefined,
          resourceIds: formModel.resourceIds || [],
          key: formModel.key,
          groupId: formModel.groupId,
        };
        if (formModel.dateRange && formModel.dateRange.length === 2) {
          model.startDate = formatISO(formModel.dateRange[0]);
          model.endDate = formatISO(formModel.dateRange[1]);
        }
        const result = await this.permissionService.savePermission(model);
        this.nt.success('nt.dataSaved');
        if (this.isCreate) {
          this.router.navigate([`/permissions/${result.id}/edit`]);
        }
      } catch (err: any) {
        this.nt.handleError(err);
      } finally {

      }
    } else {
      console.log('not valid form');
    }
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  onDatesChange($event: Date[]) {
    console.log('dates change', $event);
  }
}
